import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { RawHtml } from '../../Utilities';
const CTAButton = ({ cta, buttonStyle = 'white', size = 'lg' }) => {
  if (!cta?.text || !cta?.url) return null;
  const isInternal = cta.url.startsWith('/') || cta.url.startsWith('#');
  const sizeClass = size ? `btn-${size}` : '';
  const className = `btn btn-${buttonStyle} ${sizeClass}`.trim();
  return isInternal ? (
    <Link href={cta.url} className={className}>
      {cta.text}
    </Link>
  ) : (
    <a href={cta.url} className={className} target="_blank" rel="noopener noreferrer">
      {cta.text}
    </a>
  );
};
const CTAShell = ({ children }) => (
  <section className="section section-full">
    <div className="section__inner">
      <div className="rounded-2xl border border-neutral-300 overflow-hidden">
        {/* Gradient stripe */}
        <div className="h-1 w-full bg-gradient-to-r from-pink-400 via-purple-500 to-teal-400" />
        {/* Content */}
        <div className="p-lg">
          {children}
        </div>
      </div>
    </div>
  </section>
);
const StandaloneCTA = ({ value }) => {
  if (!value) return null;
  const {
    heading,
    subHeading,
    intro,
    cta,
    buttonStyle = 'white',
    ctaItems,
  } = value;
  const hasCtaItems = Array.isArray(ctaItems) && ctaItems.length > 0;
  const hasContent = heading || subHeading || intro;
  const hasCTA = cta?.text && cta?.url;
  if (!hasContent && !hasCTA && !hasCtaItems) return null;
  // Mode B — two sub-items
  if (hasCtaItems) {
    return (
      <CTAShell>
        {hasContent && (
          <div className="section__header">
            {heading && <div className="text-section-title">{heading}</div>}
            {subHeading && <div className="text-section-support">{subHeading}</div>}
            {intro && <div className="text-body">
              <RawHtml html={intro} />
              </div>}
          </div>
        )}
        <div className="grid-2 gap-blocks mt-lg">
          {ctaItems.map((item, index) => (
            <div key={index} className={`card surface-pad-normal flex flex-col h-full ${index === 0 ? 'card-outlined' : 'panel'}`}>
              <div className="block flex flex-col flex-1">
                <div className="block__body stack stack-normal flex-1">
                  {item.heading && <div className="text-block-title">{item.heading}</div>}
                  {item.intro && <div className="text-body">{item.intro}</div>}
                </div>
                <div className="block__footer inline-flex self-start mt-auto pt-md">
                  <CTAButton cta={item.cta} buttonStyle="secondary" size="sm" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </CTAShell>
    );
  }
  // Mode A — single CTA
  return (
    <CTAShell>
      <div className="cluster cluster-responsive cluster-between cluster-normal items-center">
        <div className="stack stack-tight">
          {heading && <div className="text-section-title">{heading}</div>}
          {subHeading && <div className="text-section-support">{subHeading}</div>}
          {intro && <div className="text-body">{intro}</div>}
        </div>
        <CTAButton cta={cta} buttonStyle={buttonStyle} size="lg" />
      </div>
    </CTAShell>
  );
};
StandaloneCTA.propTypes = {
  value: PropTypes.shape({
    heading: PropTypes.string,
    subHeading: PropTypes.string,
    intro: PropTypes.string,
    cta: PropTypes.shape({
      text: PropTypes.string,
      url: PropTypes.string,
    }),
    buttonStyle: PropTypes.oneOf(['primary', 'secondary', 'outline', 'white']),
    ctaItems: PropTypes.arrayOf(
      PropTypes.shape({
        heading: PropTypes.string,
        intro: PropTypes.string,
        cta: PropTypes.shape({
          text: PropTypes.string,
          url: PropTypes.string,
        }),
        buttonStyle: PropTypes.oneOf(['primary', 'secondary', 'outline', 'white']),
      })
    ),
  }),
};
export default StandaloneCTA;